import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-create-eks-operations",
  templateUrl: "./create-eks.operations.components.html",
  styleUrls: ["./create-eks.operations.components.css"],
})
export class CreateEksOperationsComponent implements OnInit, OnDestroy {
  private role: string = (localStorage.getItem("role") || "").toLowerCase();
  urlPrefix: string = this.role == "admin" ? "admin" : "client";
  apiBaseUrl: string = environment.apiURL || APIService.API_ENDPOINTV3;
  accountId: string = "";
  regionId: string = "";
  currentMessage: Subscription;

  loading: any = { submit: false };
  clusterPricing: any = null;
  pricingError: string = "";
  showPricingStep: boolean = false;
  saveSuccessMessage: string = "";
  saveError: string = "";
  options: any = { vpcs: [], subnets: [], securityGroups: [], kubernetesVersions: [], clusterRoles: [] };

  form: any = {
    cluster_name: "",
    launch_justification: "",
    vpc_id: null,
    cluster_role_arn: null,
    subnet_ids: [],
    security_group_ids: [],
    kubernetes_version: null,
    endpoint_public_access: true,
    endpoint_private_access: false,
    tags: [{ key: "", value: "" }],
  };

  constructor(private apiServer: APIService, private notifier: NotifierService, private router: Router) {}

  ngOnInit(): void {
    this.accountId = this.normalize(localStorage.getItem("accountId"));
    this.regionId = this.normalize(localStorage.getItem("regionId"));

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (!msg) return;
      try {
        const d = JSON.parse(msg);
        if (d.value == null || d.source != "notifier") return;
        if (d.key == "accountId") {
          this.accountId = this.normalize(d.value);
          this.loadMasters();
        } else if (d.key == "regionId") {
          this.regionId = this.normalize(d.value);
          this.loadMasters();
        }
      } catch {}
    });

    if (this.accountId && this.regionId) {
      this.loadMasters();
    }
  }

  ngOnDestroy(): void {
    if (this.currentMessage) this.currentMessage.unsubscribe();
  }

  headers() {
    return { Authorization: localStorage.getItem("t") };
  }

  private normalize(value: any): string {
    const v = (value ?? "").toString().trim();
    if (!v || v.toLowerCase() == "null" || v.toLowerCase() == "undefined") return "";
    return v;
  }

  private getRequesterId(): string {
    return this.normalize(localStorage.getItem("eId"));
  }

  private getRequesterEmail(): string {
    return (
      this.normalize(localStorage.getItem("email")) ||
      this.normalize(localStorage.getItem("userEmail")) ||
      this.normalize(localStorage.getItem("eId"))
    );
  }

  private buildApiUrl(path: string) {
    return `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
  }

  async get(url: string) {
    try {
      const r = await this.apiServer.getDataPromis(url, {}, this.headers());
      if (typeof r == "string") return null;
      return r;
    } catch {
      return null;
    }
  }

  async loadVpcs() {
    if (!this.accountId || !this.regionId) return;
    this.loading.vpcs = true;
    const url = this.buildApiUrl(
      `apis/ec2-config-2/api/ec2/vpcs?account_id=${this.accountId}&region=${this.regionId}`
    );
    const r = await this.get(url);
    this.options.vpcs = r?.vpcs || [];
    this.loading.vpcs = false;
  }

  async loadMasters() {
    await Promise.all([this.loadVpcs(), this.loadKubernetesVersions(), this.loadClusterRoles()]);
  }

  async loadKubernetesVersions() {
    if (!this.accountId || !this.regionId) return;
    this.loading.kubernetesVersions = true;
    const url = this.buildApiUrl(
      `apis/eks-master-data/api/eks/kubernetes-versions?account_id=${this.accountId}&region=${this.regionId}`
    );
    const r = await this.get(url);
    this.options.kubernetesVersions = r?.kubernetes_versions || [];
    this.loading.kubernetesVersions = false;
  }

  async loadClusterRoles() {
    if (!this.accountId || !this.regionId) return;
    this.loading.clusterRoles = true;
    const url = this.buildApiUrl(
      `apis/eks-master-data/api/eks/cluster-roles?account_id=${this.accountId}&region=${this.regionId}&service_name=eks.amazonaws.com`
    );
    const r = await this.get(url);
    const roles = r?.cluster_roles || [];
    this.options.clusterRoles = roles.filter((x: any) => !this.isServiceLinkedRoleArn(x?.role_arn));
    if (this.form.cluster_role_arn && this.isServiceLinkedRoleArn(this.form.cluster_role_arn)) {
      this.form.cluster_role_arn = null;
    }
    this.loading.clusterRoles = false;
  }

  private isServiceLinkedRoleArn(arn: any): boolean {
    const v = (arn || "").toString();
    return v.includes(":role/aws-service-role/") || v.includes("/aws-service-role/");
  }

  async onVpcChange() {
    this.form.subnet_ids = [];
    this.form.security_group_ids = [];
    this.options.subnets = [];
    this.options.securityGroups = [];
    if (!this.form.vpc_id) return;
    await Promise.all([this.loadSubnets(), this.loadSecurityGroups()]);
  }

  async loadSubnets() {
    this.loading.subnets = true;
    const url = this.buildApiUrl(
      `apis/ec2-config-2/api/ec2/subnets?account_id=${this.accountId}&region=${this.regionId}&vpc_id=${this.form.vpc_id}`
    );
    const r = await this.get(url);
    this.options.subnets = r?.subnets || [];
    this.loading.subnets = false;
  }

  async loadSecurityGroups() {
    this.loading.securityGroups = true;
    const url = this.buildApiUrl(
      `apis/ec2-config-2/api/ec2/security-groups?account_id=${this.accountId}&region=${this.regionId}&vpc_id=${this.form.vpc_id}`
    );
    const r = await this.get(url);
    this.options.securityGroups = r?.security_groups || [];
    this.loading.securityGroups = false;
  }

  addTag() {
    this.form.tags.push({ key: "", value: "" });
  }

  removeTag(index: number) {
    if (this.form.tags.length === 1) {
      this.form.tags[0] = { key: "", value: "" };
      return;
    }
    this.form.tags.splice(index, 1);
  }

  canSubmit(): boolean {
    const asText = (v: any) => (v ?? "").toString().trim();
    const subnets = (this.form.subnet_ids || []).map((x: string) => asText(x)).filter((x: string) => !!x);
    const sgs = (this.form.security_group_ids || []).map((x: string) => asText(x)).filter((x: string) => !!x);
    const tagsValid = (this.form.tags || []).every((t: any) => !asText(t?.key) || !!asText(t?.value));

    return !!(
      this.accountId &&
      this.regionId &&
      this.getRequesterId() &&
      this.getRequesterEmail() &&
      asText(this.form.launch_justification) &&
      asText(this.form.cluster_name) &&
      this.form.vpc_id &&
      asText(this.form.cluster_role_arn) &&
      asText(this.form.kubernetes_version) &&
      subnets.length > 0 &&
      sgs.length > 0 &&
      tagsValid
    );
  }

  async goToPricing() {
    if (!this.canSubmit() || this.loading.pricing) {
      this.notifier.alert("Warning", "", "Please fill all required fields.", "warning", 3000);
      return;
    }
    if (this.isServiceLinkedRoleArn(this.form.cluster_role_arn)) {
      this.notifier.alert(
        "Warning",
        "",
        "Cluster Role ARN cannot be an AWS service-linked role. Please select a customer-managed IAM role.",
        "warning",
        4500
      );
      return;
    }

    this.loading.pricing = true;
    this.pricingError = "";
    this.clusterPricing = null;
    this.saveSuccessMessage = "";
    this.saveError = "";
    this.showPricingStep = true;
    try {
      const url = this.buildApiUrl(`apis/eks_launch/api/eks/cluster-pricing`);
      const body = { region: this.regionId, support_tier: "STANDARD" };
      const result: any = await this.apiServer.postDataPromis(url, body, this.headers());
      if (!result || typeof result == "string") throw new Error("Invalid response");
      this.clusterPricing = result;
    } catch {
      this.pricingError = "Unable to fetch cluster pricing right now.";
    } finally {
      this.loading.pricing = false;
    }
  }

  backToForm() {
    if (this.loading.submit || this.loading.pricing) return;
    this.showPricingStep = false;
    this.saveSuccessMessage = "";
    this.saveError = "";
  }

  buildRequestBody() {
    return {
      requester_id: this.getRequesterId(),
      requester_email: this.getRequesterEmail(),
      launch_justification: this.form.launch_justification.trim(),
      jira_project_key: this.normalize(localStorage.getItem("swayam_jira_project_key") || localStorage.getItem("jira_project_key")),
      jira_organization_key: this.normalize(
        localStorage.getItem("swayam_jira_org_name") || localStorage.getItem("jira_organization_key") || localStorage.getItem("swayam_jira_org_key")
      ),
      request_payload: {
        aws_account_id: this.accountId,
        region: this.regionId,
        cluster_name: this.form.cluster_name.trim(),
        vpc_id: this.form.vpc_id,
        cluster_role_arn: this.form.cluster_role_arn.trim(),
        subnet_ids: this.form.subnet_ids.map((x: string) => x.trim()).filter((x: string) => !!x),
        security_group_ids: this.form.security_group_ids
          .map((x: string) => x.trim())
          .filter((x: string) => !!x),
        kubernetes_version: this.form.kubernetes_version.trim(),
        endpoint_public_access: !!this.form.endpoint_public_access,
        endpoint_private_access: !!this.form.endpoint_private_access,
        tags: this.form.tags
          .map((t: any) => ({ key: (t.key || "").trim(), value: (t.value || "").trim() }))
          .filter((t: any) => t.key && t.value),
      },
    };
  }

  async submitRequest() {
    this.saveSuccessMessage = "";
    this.saveError = "";
    if (!this.canSubmit() || this.loading.submit) {
      this.notifier.alert("Warning", "", "Please fill all required fields.", "warning", 3000);
      return;
    }
    if (this.isServiceLinkedRoleArn(this.form.cluster_role_arn)) {
      this.notifier.alert(
        "Warning",
        "",
        "Cluster Role ARN cannot be an AWS service-linked role. Please select a customer-managed IAM role.",
        "warning",
        4500
      );
      return;
    }

    this.loading.submit = true;

    try {
      const url = this.buildApiUrl(`apis/eks_launch/api/eks/save-request`);
      const result: any = await this.apiServer.postDataPromis(url, this.buildRequestBody(), this.headers());
      if (!result || typeof result == "string" || result.detail || result.error || (result.status !== undefined && result.status !== "1" && result.status !== 1) || (result.s !== undefined && result.s !== "1" && result.s !== 1)) {
        let errorMsg = result?.message || result?.msg || (Array.isArray(result?.detail) ? result.detail[0]?.msg : null) || result?.error || "Failed to submit EKS request.";
        if (typeof errorMsg === "string" && errorMsg.startsWith("Value error, ")) {
          errorMsg = errorMsg.replace("Value error, ", "");
        }
        throw new Error(errorMsg);
      }
      this.saveSuccessMessage = result.message || "EKS request submitted successfully.";
      this.notifier.alert("Success", "", result.message || "EKS request submitted successfully.", "success", 4000);
      this.router.navigate(["/dash/operations/eks-dashboard"]);
    } catch (err: any) {
      this.saveError = err?.message || "Failed to submit EKS request.";
      this.notifier.alert("Error", "", this.saveError, "error", 4000);
    } finally {
      this.loading.submit = false;
    }
  }
}
